import React, { useCallback, useEffect, useMemo, useState } from "react";
// CSS
import styles from "./CommentsPopup.module.css";
// Components
import Button from "../Button/Button";
import FloatingLabelTextArea from "../Form/FloatingLabelTextArea/FloatingLabelTextArea";
import CommentItem from "./CommentItem";
// Types
import { Comment } from "./CommentsPopup";
// API client
import { axiosInstance } from "../../../Authentication/AxiosInterceptor";
import { toast } from "react-toastify";
import DropDownList from "../DropDownList/DropDownList";
import { IDropDownListItem } from "../../../Types/DropDownList/IDropDownListItem";
import { CommentType, CreateCommentRequest } from "../../../Types/CreateCommentRequest";

/** Develop Area comment type – matches backend DevelopAreaCommentType */
export type DevelopAreaCommentType = "Reflections" | "LineManager" | "Mentor" | "Other";

type ReusableCommentsPopupProps = {
  contextId: string;
  /** Required for summarise mode; not used for develop mode */
  authorizationContextId?: string;

  /** Optional permissions so the component stays reusable without Redux */
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;

  /** Optional hook for parent components */
  onCommentsChange?: (comments: Comment[]) => void;

  progressMode: boolean;

  /**
   * API mode: "summarise" uses criteria-based endpoints; "develop" uses Develop Area endpoints.
   * When "develop", commentType is required and authorizationContextId is ignored.
   */
  apiMode?: "summarise" | "develop";
  /** Required when apiMode is "develop" – selects which comment list (Reflections, LineManager, Mentor, Other) */
  commentType?: DevelopAreaCommentType;
};

/** Normalizes API comment to frontend Comment (adds Editable) */
function toFrontendComment(raw: { id?: string; Id?: string; CommentText?: string; Commenter?: string; DateMade?: string | Date }, canEdit: boolean): Comment {
  const id = raw.id ?? raw.Id ?? "";
  const dateMade = raw.DateMade ? (typeof raw.DateMade === "string" ? new Date(raw.DateMade) : raw.DateMade) : new Date();
  return {
    id,
    DateMade: dateMade,
    CommentText: raw.CommentText ?? "",
    Commenter: raw.Commenter ?? "",
    Editable: canEdit,
  };
}

const ReusableCommentsPopup: React.FC<ReusableCommentsPopupProps> = ({
  contextId,
  authorizationContextId,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  onCommentsChange,
  progressMode,
  apiMode = "summarise",
  commentType,
}) => {
  const isDevelop = apiMode === "develop";
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [statusOptions] = useState<IDropDownListItem[] | undefined>(progressMode? [
    { text: "Not Met", colour: "#E25A74", textColour: "#FFFFFF", value: "Not Met" },
    { text: "Partially Met", colour: "#EAB33C", textColour: "#000000", value: "Partially Met" },
    { text: "Met", colour: "#52BFA2", textColour: "#FFFFFF", value: "Met" },
    { text: "Exceeded", colour: "#24394F", textColour: "#FFFFFF", value: "Exceeded" },
  ] : undefined);

  const [selectedStatus, setSelectedStatus] = useState<string>();

  const setCommentsAndNotify = useCallback(
    (next: Comment[] | ((prev: Comment[]) => Comment[])) => {
      setComments((prev) => {
        const resolved = typeof next === "function" ? (next as any)(prev) : next;
        onCommentsChange?.(resolved);
        return resolved;
      });
    },
    [onCommentsChange]
  );

  const fetchComments = useCallback(async () => {
    if (!contextId) {
      setCommentsAndNotify([]);
      return;
    }

    setLoading(true);
    try {
      if (isDevelop && commentType) {
        const res = await axiosInstance.get<unknown[]>(
          `GetDevelopAreaComments/${contextId}/${commentType}`
        );
        const mapped = (res.data ?? []).map((raw) => toFrontendComment(raw as Record<string, unknown>, canEdit));
        setCommentsAndNotify(mapped);
      } else {
        const res = await axiosInstance.get<Comment[]>(
          `GetSummariseCommentsByCriteriaId/${contextId}/${progressMode ? CommentType.ProgressNote : CommentType.Comment}`
        );
        setCommentsAndNotify(res.data ?? []);
      }
    } catch (e) {
      toast.error("Failed to load comments");
      setCommentsAndNotify([]);
    } finally {
      setLoading(false);
    }
  }, [contextId, setCommentsAndNotify, isDevelop, commentType, canEdit, progressMode]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!contextId) {
        setCommentsAndNotify([]);
        return;
      }
      if (isDevelop && !commentType) {
        setCommentsAndNotify([]);
        return;
      }

      setLoading(true);
      try {
        if (isDevelop && commentType) {
          const res = await axiosInstance.get<unknown[]>(
            `GetDevelopAreaComments/${contextId}/${commentType}`
          );
          const mapped = (res.data ?? []).map((raw) => toFrontendComment(raw as Record<string, unknown>, canEdit));
          if (!cancelled) setCommentsAndNotify(mapped);
        } else {
          const res = await axiosInstance.get<Comment[]>(
            `GetSummariseCommentsByCriteriaId/${contextId}/${progressMode ? CommentType.ProgressNote : CommentType.Comment}`
          );
          if (!cancelled) setCommentsAndNotify(res.data ?? []);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load comments");
          setCommentsAndNotify([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contextId, setCommentsAndNotify, isDevelop, commentType, progressMode, canEdit]);

  const isSendDisabled = useMemo(() => {
    // TODO: remove bypass – temporarily enable Send for develop mode testing (only disabled when saving)
    if (isDevelop) return saving;
    if (!canCreate) return true;
    if (!contextId) return true;
    if (isDevelop) {
      if (!commentType) return true;
    } else {
      if (!authorizationContextId) return true;
    }
    if (!commentText.trim()) return true;
    return saving;
  }, [canCreate, contextId, authorizationContextId, commentText, saving, isDevelop, commentType]);

  const handleCreateComment = useCallback(async () => {
    if (isSendDisabled || !contextId) return;
    if (!isDevelop && !authorizationContextId) return;
    if (isDevelop && !commentType) return;

    if (progressMode && !selectedStatus) {
      toast.error("Please select a progress status");
      return;
    }

    const text = commentText.trim();
    setSaving(true);

    try {
      if (isDevelop && commentType) {
        await axiosInstance.patch<Record<string, unknown>>(
          `PatchDevelopAreaComment/${contextId}/${commentType}`,
          { action: "create", comment: { CommentText: text } }
        );
        // Always refresh for develop mode so local state shows resolved display names, not raw subs.
        await fetchComments();
      } else {
        const request: CreateCommentRequest = {
          text: text,
          progressStatus: selectedStatus,
          type: progressMode ? CommentType.ProgressNote : CommentType.Comment,
        };

        const res = await axiosInstance.post<Comment>(
          `CreateSummariseComment/${contextId}/${authorizationContextId}`,
          request
        );

        if (res.data) {
          setCommentsAndNotify((prev) => [...prev, res.data]);
        } else {
          await fetchComments();
        }
      }

      setCommentText("");
    } catch {
      toast.error("Failed to create comment");
    } finally {
      setSaving(false);
    }
  }, [
    isSendDisabled,
    contextId,
    authorizationContextId,
    commentText,
    fetchComments,
    setCommentsAndNotify,
    progressMode,
    selectedStatus,
    isDevelop,
    commentType,
    canEdit,
  ]);

  const handleEditComment = useCallback(
    async (commentId: string, text: string, status?: string) => {
      if (!canEdit) return;

      const trimmed = text.trim();
      if (!trimmed) return;

      setSaving(true);
      try {
        if (isDevelop && commentType && contextId) {
          await axiosInstance.patch(
            `PatchDevelopAreaComment/${contextId}/${commentType}`,
            { action: "update", commentId, comment: { CommentText: trimmed, ProgressStatus: status || undefined } }
          );
          setCommentsAndNotify((prev) =>
            prev.map((c) =>
              c.id === commentId ? { ...c, CommentText: trimmed, ProgressStatus: status as Comment["ProgressStatus"] } : c
            )
          );
        } else {
          const request: CreateCommentRequest = {
            text: trimmed,
            progressStatus: status,
            type: progressMode ? CommentType.ProgressNote : CommentType.Comment,
          };

          await axiosInstance.post(`EditSummariseComment/${commentId}`, request);

          setCommentsAndNotify((prev) =>
            prev.map((c) =>
              c.id === commentId ? { ...c, CommentText: trimmed, ProgressStatus: status as Comment["ProgressStatus"] } : c
            )
          );
        }
      } catch {
        toast.error("Failed to edit comment");
      } finally {
        setSaving(false);
      }
    },
    [canEdit, setCommentsAndNotify, isDevelop, commentType, contextId, progressMode]
  );

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!canDelete) return;

      setSaving(true);
      try {
        if (isDevelop && commentType && contextId) {
          await axiosInstance.patch(
            `PatchDevelopAreaComment/${contextId}/${commentType}`,
            { action: "delete", commentId }
          );
        } else {
          await axiosInstance.get(`DeleteSummariseComment/${commentId}`);
        }

        setCommentsAndNotify((prev) => prev.filter((c) => c.id !== commentId));
      } catch {
        toast.error("Failed to delete comment");
      } finally {
        setSaving(false);
      }
    },
    [canDelete, setCommentsAndNotify, isDevelop, commentType, contextId]
  );

  return (
    <div className={styles.container}>
      {/* Comment Input Area */}
      <div className={styles.inputSection}>
        <div className={styles.inputFields}>

          <FloatingLabelTextArea
          label={
            !contextId
              ? "Select an item to view comments..."
              : !isDevelop && !authorizationContextId
              ? "Missing authorization context..."
              : isDevelop && !commentType
              ? "Missing comment type..."
              : "Add your comment..."
          }
          value={commentText}
          onChange={(value) => setCommentText(value)}
        />

        {progressMode && statusOptions &&
          <div className={styles.statusDropdown}>
            <p>Select your status...</p>
            <DropDownList value={selectedStatus || ""} placeholder={""} data={statusOptions} onChange={(value) => setSelectedStatus(value.value)}/>
          </div>
        }

        </div>
        
        <Button
          type="button"
          clickHandler={handleCreateComment}
          className={styles.sendButton}
          disabled={isDevelop ? false : isSendDisabled}
        >
          {saving ? "Sending..." : "Send"}
        </Button>
      </div>

      {/* Existing comments */}
      <div className={styles.commentsList}>
        {loading ? (
          <div>Loading comments…</div>
        ) : comments.length === 0 ? (
          <div>No comments yet.</div>
        ) : (
          comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onEdit={handleEditComment}
              onDelete={handleDeleteComment}
              canEdit={canEdit}
              canDelete={canDelete}
              statusOptions={statusOptions}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ReusableCommentsPopup;
